import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import { olreadiaService } from "services/olreadiaService";
import {
  ColorPickerField,
  FormField,
  ImageUploadBox,
  ProgressHeader,
  RepeatableSection,
  SocialLinksFields,
  StatsStep,
  WizardPageShell,
  firstArray,
  firstValue,
  useImageUpload,
} from "./WebsiteWizard";
import WebsiteGenerateModal from "./WebsiteGenerateModal";
import "./WebsiteWizard.css";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { TextArea } = Input;
const { Text } = Typography;

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Food & Beverage",
  "Home Services",
  "Retail",
  "Education",
  "Finance",
  "Legal",
  "Real Estate",
  "Beauty & Wellness",
  "Other",
];

function slugify(value) {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const STEPS = [
  {
    id: "basics",
    label: "Basics",
    icon: "🏢",
    description: "Name, description & branding",
  },
  {
    id: "hero",
    label: "Hero",
    icon: "✨",
    description: "Your website's first impression",
  },
  {
    id: "about",
    label: "About",
    icon: "📖",
    description: "Your story & mission",
  },
  {
    id: "services",
    label: "Services",
    icon: "⚡",
    description: "What you offer",
  },
  {
    id: "features",
    label: "Features",
    icon: "⚡",
    description: "Why clients should choose you",
  },
  {
    id: "stats",
    label: "Stats",
    icon: "📊",
    description: "Your numbers & achievements",
  },
  {
    id: "testimonials",
    label: "Testimonials",
    icon: "💬",
    description: "What clients say",
  },
  {
    id: "team",
    label: "Team",
    icon: "👥",
    description: "The people behind your business",
  },
  {
    id: "contact",
    label: "Contact & FAQ",
    icon: "📍",
    description: "How clients reach you",
  },
];

const OPTIONAL_STEPS = [2, 3, 4, 5, 6, 7];

function emptyFormData(workspace) {
  return {
    businessName: workspace?.name ?? "",
    slug: "",
    slugTouched: false,
    description: workspace?.description ?? "",
    tagline: "",
    industry: "",
    primaryColor: "#27ae30",
    secondaryColor: "#C9F2CB",
    logoUri: workspace?.logoUrl ?? "",

    headline: workspace?.name ?? "",
    subheadline: workspace?.description ?? "",
    heroTagline: workspace?.description ?? "",
    heroImageUri: "",
    ctaText: "Get in Touch",

    shortDescription: "",
    story: "",
    mission: "",
    vision: "",
    aboutImageUri: "",

    services: [],
    features: [],
    stats: [],
    testimonials: [],
    team: [],

    phone: workspace?.contactInformation?.primaryPhone ?? "",
    email: workspace?.contactInformation?.primaryEmail ?? "",
    city: workspace?.locationInformation?.city ?? "",
    address: workspace?.locationInformation?.addressLine1 ?? "",
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
    faqs: [],
  };
}

function buildBusinessOverrides(formData) {
  return {
    businessName: formData.businessName,
    slug: formData.slug,
    description: formData.description,
    tagline: formData.tagline,
    industry: formData.industry,
    primaryColor: formData.primaryColor,
    secondaryColor: formData.secondaryColor,
    logoUrl: formData.logoUri,
    heroImageUrl: formData.heroImageUri,
    headline: formData.headline,
    subheadline: formData.subheadline,
    heroTagline: formData.heroTagline,
    ctaText: formData.ctaText,
    shortDescription: formData.shortDescription,
    story: formData.story,
    mission: formData.mission,
    vision: formData.vision,
    aboutImageUrl: formData.aboutImageUri,
    services: JSON.stringify(formData.services),
    features: JSON.stringify(
      formData.features.map((f) => ({
        title: f.title,
        description: f.description,
        imageUrl: f.imageUri,
      })),
    ),
    stats: JSON.stringify(formData.stats),
    testimonials: JSON.stringify(
      formData.testimonials.map((t) => ({
        name: t.name,
        role: t.role,
        content: t.content,
        avatarUrl: t.avatarUri,
      })),
    ),
    team: JSON.stringify(
      formData.team.map((t) => ({
        name: t.name,
        role: t.role,
        description: t.bio,
        imageUrl: t.avatarUri,
      })),
    ),
    phone: formData.phone,
    email: formData.email,
    city: formData.city,
    address: formData.address,
    facebook: formData.facebook,
    instagram: formData.instagram,
    twitter: formData.twitter,
    linkedin: formData.linkedin,
    youtube: formData.youtube,
    faqs: JSON.stringify(formData.faqs),
  };
}

// steps
function Step1Basics({
  data,
  onChange,
  uploadingKey,
  progress,
  upload,
  slugStatus,
  slugMessage,
  mode,
}) {
  const handleSlug = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    onChange("slug", value);
    onChange("slugTouched", true);
  };

  const slugSuffix = {
    typing: null,
    checking: <Spin size="small" />,
    available: <span style={{ color: OLIVEEB_COLORS.oliveebGreen }}>✓</span>,
    taken: <span style={{ color: "#ef4444" }}>✕</span>,
    error: null,
    idle: null,
  }[slugStatus];

  const slugHelpColor =
    slugStatus === "taken"
      ? "#ef4444"
      : slugStatus === "available"
        ? OLIVEEB_COLORS.oliveebGreen
        : OLIVEEB_COLORS.textMuted;

  return (
    <div className="wiz-step">
      <FormField label="Business Name" required>
        <Input
          value={data.businessName}
          onChange={(e) => onChange("businessName", e.target.value)}
          placeholder="e.g. Acme Plumbing Co."
        />
      </FormField>

      {mode !== "regenerate" && (
        <Form.Item
          label={
            <Text
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: OLIVEEB_COLORS.oliveebGreen,
              }}
            >
              URL slug
            </Text>
          }
          style={{ marginBottom: 0 }}
          validateStatus={slugStatus === "taken" ? "error" : undefined}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              style={{
                width: 100,
                flex: "0 0 auto",
                textAlign: "center",
                borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
                fontFamily: "monospace",
                fontSize: 12,
                color: OLIVEEB_COLORS.textMuted,
                backgroundColor: "#fafafa",
              }}
              value="ovizia.com/"
              disabled
            />
            <Input
              style={{
                borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
                height: 40,
                fontSize: 13,
                fontFamily: "monospace",
              }}
              suffix={slugSuffix}
              placeholder="my-site"
              value={data.slug}
              onChange={handleSlug}
            />
          </Space.Compact>
          <Text
            style={{
              fontSize: 11,
              color: slugHelpColor,
              display: "block",
              marginBottom: 14,
            }}
          >
            {slugMessage || "Only lowercase letters, numbers, and hyphens."}
          </Text>
        </Form.Item>
      )}

      <FormField
        label="Description"
        hint="A short caption shown alongside your business name"
      >
        <TextArea
          rows={2}
          value={data.description}
          onChange={(e) => onChange("description", e.target.value)}
          placeholder="e.g. Family-owned plumbing and repair, serving the tri-state area"
          maxLength={160}
          style={{ color: "#1a1a1a" }}
        />
      </FormField>

      <ColorPickerField
        primaryColor={data.primaryColor}
        secondaryColor={data.secondaryColor}
        onChangePrimary={(primary, secondary) => {
          onChange("primaryColor", primary);
          onChange("secondaryColor", secondary);
        }}
      />

      <ImageUploadBox
        fieldKey="logo"
        slotType="NAVBAR_LOGO"
        value={data.logoUri}
        onUpload={(file) =>
          upload("logo", file, "NAVBAR_LOGO", (url) => onChange("logoUri", url))
        }
        onRemove={() => onChange("logoUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="Logo"
        hint="Square image works best (PNG or JPG)"
        aspectLabel="PNG, JPG · Square recommended"
        height={120}
      />
    </div>
  );
}

function Step2Hero({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        This is the first thing visitors see. A strong headline and image make
        the biggest impact.
      </div>

      <FormField
        label="Headline"
        required
        hint="Your main hook — bold and punchy"
      >
        <Input
          value={data.headline}
          onChange={(e) => onChange("headline", e.target.value)}
          placeholder="e.g. Fast, Reliable Plumbing You Can Trust"
          maxLength={80}
          style={{ color: "#1a1a1a" }}
        />
      </FormField>

      <FormField
        label="Subheadline"
        hint="One sentence expanding on the headline"
      >
        <TextArea
          rows={2}
          value={data.subheadline}
          onChange={(e) => onChange("subheadline", e.target.value)}
          placeholder="e.g. Serving New York homeowners since 1995 with same-day repairs."
          maxLength={160}
          style={{ color: "#1a1a1a" }}
        />
      </FormField>

      <FormField
        label="Tagline"
        hint="Short brand phrase — shown above the headline"
      >
        <Input
          value={data.heroTagline}
          onChange={(e) => onChange("heroTagline", e.target.value)}
          placeholder="e.g. Trusted by 500+ homeowners"
          maxLength={60}
          style={{ color: "#1a1a1a" }}
        />
      </FormField>

      <FormField
        label="CTA Button Text"
        hint="Text on your main call-to-action button"
      >
        <Input
          value={data.ctaText}
          onChange={(e) => onChange("ctaText", e.target.value)}
          placeholder="e.g. Get a Free Quote"
          maxLength={40}
          style={{ color: "#1a1a1a" }}
        />
      </FormField>

      <ImageUploadBox
        fieldKey="heroImage"
        slotType="BANNER"
        value={data.heroImageUri}
        onUpload={(file) =>
          upload("heroImage", file, "BANNER", (url) =>
            onChange("heroImageUri", url),
          )
        }
        onRemove={() => onChange("heroImageUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="Hero Image"
        hint="Shown at the top of your website — your best, most impactful photo"
        aspectLabel="Landscape · 16:9 recommended"
      />
    </div>
  );
}

function Step3About({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <FormField
        label="Short Description"
        required
        hint="2–3 sentences shown on your home page — your elevator pitch"
      >
        <TextArea
          rows={3}
          value={data.shortDescription}
          onChange={(e) => onChange("shortDescription", e.target.value)}
          maxLength={300}
        />
      </FormField>

      <FormField label="Full Story" hint="How you started, what drives you">
        <TextArea
          rows={4}
          value={data.story}
          onChange={(e) => onChange("story", e.target.value)}
        />
      </FormField>

      <FormField
        label="Mission Statement"
        hint="What drives you? Keep it under 2 sentences."
      >
        <TextArea
          rows={2}
          value={data.mission}
          onChange={(e) => onChange("mission", e.target.value)}
          maxLength={200}
        />
      </FormField>

      <FormField label="Vision Statement" hint="Where are you headed?">
        <TextArea
          rows={2}
          value={data.vision}
          onChange={(e) => onChange("vision", e.target.value)}
          maxLength={200}
        />
      </FormField>

      <ImageUploadBox
        fieldKey="aboutImage"
        slotType="GALLERY"
        value={data.aboutImageUri}
        onUpload={(file) =>
          upload("aboutImage", file, "GALLERY", (url) =>
            onChange("aboutImageUri", url),
          )
        }
        onRemove={() => onChange("aboutImageUri", "")}
        uploadingKey={uploadingKey}
        progress={progress}
        label="About / Team Photo"
        hint="A photo of your team, office or work in action"
        aspectLabel="Landscape · 4:3 recommended"
      />
    </div>
  );
}

function Step4Services({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Specific services produce far better AI content than generic
        descriptions.
      </div>
      <RepeatableSection
        items={data.services}
        onChange={(items) => onChange("services", items)}
        emptyItem={{ title: "", description: "", imageUri: "" }}
        itemLabel="Service"
        max={8}
        addLabel={{
          first: "Add your first service",
          more: "Add another service",
        }}
        emptyLabel="No services added. The AI will generate generic services based on your industry."
        renderItem={(item, update, index) => (
          <>
            <FormField label="Service name" required>
              <Input
                value={item.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Emergency Pipe Repair"
              />
            </FormField>
            <FormField label="Description" hint="1–2 sentences">
              <TextArea
                rows={2}
                value={item.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormField>
            <ImageUploadBox
              fieldKey={`service_${index}`}
              slotType="GALLERY"
              value={item.imageUri}
              onUpload={(file) =>
                upload(`service_${index}`, file, "GALLERY", (url) =>
                  update({ imageUri: url }),
                )
              }
              onRemove={() => update({ imageUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Service Image"
              hint="Optional"
              aspectLabel="4:3 recommended"
              height={120}
            />
          </>
        )}
      />
    </div>
  );
}

function Step5Features({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Specific features that make you unique — these answer the "Why Choose
        Us" question.
      </div>
      <RepeatableSection
        items={data.features}
        onChange={(items) => onChange("features", items)}
        emptyItem={{ title: "", description: "", imageUri: "" }}
        itemLabel="Feature"
        max={8}
        addLabel={{
          first: "Add your first feature",
          more: "Add another feature",
        }}
        emptyLabel="No features added. The AI will generate generic features based on your industry."
        renderItem={(item, update, index) => (
          <>
            <FormField label="Feature name" required>
              <Input
                value={item.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Expert Team"
              />
            </FormField>
            <FormField label="Description" hint="1–2 sentences">
              <TextArea
                rows={2}
                value={item.description}
                onChange={(e) => update({ description: e.target.value })}
              />
            </FormField>
            <ImageUploadBox
              fieldKey={`feature_${index}`}
              slotType="GALLERY"
              value={item.imageUri}
              onUpload={(file) =>
                upload(`feature_${index}`, file, "GALLERY", (url) =>
                  update({ imageUri: url }),
                )
              }
              onRemove={() => update({ imageUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Feature Image"
              hint="Optional"
              aspectLabel="4:3 recommended"
              height={120}
            />
          </>
        )}
      />
    </div>
  );
}

function Step7Testimonials({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Real quotes from happy clients build trust instantly. Even one strong
        testimonial makes a difference.
      </div>
      <RepeatableSection
        items={data.testimonials}
        onChange={(items) => onChange("testimonials", items)}
        emptyItem={{ name: "", role: "", content: "", avatarUri: "" }}
        itemLabel="Testimonial"
        addLabel={{
          first: "Add your first testimonial",
          more: "Add another testimonial",
        }}
        emptyLabel="No testimonials added. The AI will generate placeholder testimonials."
        renderItem={(item, update, index) => (
          <>
            <FormField label="Quote" required>
              <TextArea
                rows={2}
                value={item.content}
                onChange={(e) => update({ content: e.target.value })}
              />
            </FormField>
            <div className="wiz-two-col">
              <FormField label="Name" required>
                <Input
                  value={item.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Sarah J."
                />
              </FormField>
              <FormField label="Role / Title">
                <Input
                  value={item.role}
                  onChange={(e) => update({ role: e.target.value })}
                  placeholder="e.g. Homeowner"
                />
              </FormField>
            </div>
            <ImageUploadBox
              fieldKey={`avatar_${index}`}
              slotType="PROFILE_PHOTO"
              value={item.avatarUri}
              onUpload={(file) =>
                upload(`avatar_${index}`, file, "PROFILE_PHOTO", (url) =>
                  update({ avatarUri: url }),
                )
              }
              onRemove={() => update({ avatarUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Client Photo"
              hint="Optional — square photo"
              aspectLabel="Square · 1:1"
              height={100}
              circle
            />
          </>
        )}
      />
    </div>
  );
}

function Step8Team({ data, onChange, uploadingKey, progress, upload }) {
  return (
    <div className="wiz-step">
      <div className="wiz-intro">
        Putting faces to your business builds trust. Add the key people your
        clients will work with.
      </div>
      <RepeatableSection
        items={data.team}
        onChange={(items) => onChange("team", items)}
        emptyItem={{ name: "", role: "", bio: "", avatarUri: "" }}
        itemLabel="Team Member"
        addLabel={{
          first: "Add your first team member",
          more: "Add another team member",
        }}
        emptyLabel="No team members added. The AI will generate placeholder profiles."
        renderItem={(item, update, index) => (
          <>
            <div className="wiz-two-col">
              <FormField label="Full Name" required>
                <Input
                  value={item.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Alex Johnson"
                />
              </FormField>
              <FormField label="Role / Title" required>
                <Input
                  value={item.role}
                  onChange={(e) => update({ role: e.target.value })}
                  placeholder="e.g. CEO"
                />
              </FormField>
            </div>
            <FormField label="Bio" hint="1–2 sentences">
              <TextArea
                rows={2}
                value={item.bio}
                onChange={(e) => update({ bio: e.target.value })}
                maxLength={200}
              />
            </FormField>
            <ImageUploadBox
              fieldKey={`team_${index}`}
              slotType="PROFILE_PHOTO"
              value={item.avatarUri}
              onUpload={(file) =>
                upload(`team_${index}`, file, "PROFILE_PHOTO", (url) =>
                  update({ avatarUri: url }),
                )
              }
              onRemove={() => update({ avatarUri: "" })}
              uploadingKey={uploadingKey}
              progress={progress}
              label="Photo"
              hint="Optional — square headshot works best"
              aspectLabel="Square · 1:1"
              height={100}
              circle
            />
          </>
        )}
      />
    </div>
  );
}

function Step9ContactFaq({ data, onChange }) {
  return (
    <div className="wiz-step">
      <Card size="small" className="wiz-card" title="Contact Details">
        <FormField label="Phone Number" required>
          <Input
            value={data.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            placeholder="e.g. +1 555 123 4567"
          />
        </FormField>
        <FormField label="Email Address" required>
          <Input
            value={data.email}
            onChange={(e) => onChange("email", e.target.value)}
            placeholder="e.g. hello@yourbusiness.com"
          />
        </FormField>
        <FormField label="City / Location" required>
          <Input
            value={data.city}
            onChange={(e) => onChange("city", e.target.value)}
            placeholder="e.g. New York, NY"
          />
        </FormField>
        <FormField label="Full Address" hint="Optional">
          <Input
            value={data.address}
            onChange={(e) => onChange("address", e.target.value)}
            placeholder="e.g. 123 Main St, New York, NY 10001"
          />
        </FormField>
      </Card>

      <Card size="small" className="wiz-card" title="Social Links">
        <SocialLinksFields data={data} onChange={onChange} />
      </Card>

      <Card size="small" className="wiz-card" title="FAQs">
        <RepeatableSection
          items={data.faqs}
          onChange={(items) => onChange("faqs", items)}
          emptyItem={{ question: "", answer: "" }}
          itemLabel="FAQ"
          addLabel={{ first: "Add a FAQ", more: "Add another FAQ" }}
          renderItem={(item, update) => (
            <>
              <FormField label="Question" required>
                <Input
                  value={item.question}
                  onChange={(e) => update({ question: e.target.value })}
                />
              </FormField>
              <FormField label="Answer" required>
                <TextArea
                  rows={2}
                  value={item.answer}
                  onChange={(e) => update({ answer: e.target.value })}
                />
              </FormField>
            </>
          )}
        />
      </Card>
    </div>
  );
}

// main
export default function WebsiteBuildBusiness() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const websiteId = searchParams.get("websiteId");
  const mode = searchParams.get("mode");

  const [template, setTemplate] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(mode === "regenerate");
  const touchedRef = useRef(new Set());

  const [slugStatus, setSlugStatus] = useState("idle"); // idle | checking | available | taken | error
  const [slugMessage, setSlugMessage] = useState("");
  const slugCheckSeq = useRef(0);
  const slugDebounceRef = useRef(null);

  const [formData, setFormData] = useState(() =>
    emptyFormData(selectedWorkspace),
  );

  useEffect(() => {
    if (mode === "regenerate") return;
    if (formData.slugTouched) return;
    const derived = slugify(formData.businessName);
    if (derived !== formData.slug) {
      setFormData((prev) => ({ ...prev, slug: derived }));
    }
  }, [formData.businessName, formData.slugTouched]);

  const runSlugCheck = useCallback(
    async (slug) => {
      setSlugStatus("checking");
      const seq = ++slugCheckSeq.current;
      try {
        const res = await olreadiaService.websites.checkSlug(slug, actorId);
        if (seq !== slugCheckSeq.current) return;
        setSlugStatus(res.available ? "available" : "taken");
        setSlugMessage(res.message);
      } catch {
        if (seq !== slugCheckSeq.current) return;
        setSlugStatus("error");
        setSlugMessage("Could not check slug availability right now.");
      }
    },
    [actorId],
  );

  // debounced check whenever slug changes
  useEffect(() => {
    if (mode === "regenerate") return;
    const slug = formData.slug;

    if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);

    if (!slug || slug.length < 3) {
      setSlugStatus("idle");
      setSlugMessage("");
      slugCheckSeq.current++; // invalidate any in-flight check
      return;
    }

    // show "typing" (not "checking") immediately — no spinner-looks-active confusion
    setSlugStatus("typing");
    slugCheckSeq.current++; // invalidate any in-flight check from a prior value

    slugDebounceRef.current = setTimeout(() => {
      runSlugCheck(slug);
    }, 700); // bumped from 400ms

    return () => {
      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
    };
  }, [formData.slug, runSlugCheck]);

  const { uploadingKey, progress, upload } = useImageUpload((file, slotType) =>
    olreadiaService.websites.uploadImageForWorkspace(
      selectedWorkspaceId,
      file,
      slotType,
      actorId,
    ),
  );

  useEffect(() => {
    if (!templateId) return;
    olreadiaService.websites
      .getTemplate(templateId, actorId)
      .then(setTemplate)
      .catch(() => {});
  }, [templateId, actorId]);

  // prefill from existing content when regenerating
  useEffect(() => {
    if (mode !== "regenerate" || !websiteId) return;
    let cancelled = false;

    (async () => {
      try {
        const activeContent = await olreadiaService.websites.activeContent(
          websiteId,
          actorId,
        );
        let content = { sections: [] };
        try {
          content = JSON.parse(activeContent.contentJson);
        } catch {
          // ignore malformed content — form stays at defaults
        }
        if (cancelled) return;

        const section = (type) =>
          content.sections?.find((s) => s.type === type)?.data;
        const hero = section("HERO");
        const about = section("ABOUT");
        const contact = section("CONTACT");
        const servicesData = section("SERVICES");
        const featuresData = section("FEATURES");
        const statsData = section("STATS");
        const testimonialsData = section("TESTIMONIALS");
        const teamData = section("TEAM");
        const faqData = section("FAQ");
        const touched = touchedRef.current;

        setFormData((prev) => ({
          ...prev,
          businessName: touched.has("businessName")
            ? prev.businessName
            : firstValue(about?.businessName, prev.businessName),

          description: touched.has("description")
            ? prev.description
            : firstValue(about?.caption, prev.description),
          tagline: touched.has("tagline")
            ? prev.tagline
            : firstValue(about?.title, prev.tagline),
          logoUri: touched.has("logoUri")
            ? prev.logoUri
            : firstValue(content.logo?.imageUrl, prev.logoUri),
          primaryColor:
            content.themeSettings?.primaryColor ?? prev.primaryColor,
          secondaryColor:
            content.themeSettings?.secondaryColor ?? prev.secondaryColor,

          headline: touched.has("headline")
            ? prev.headline
            : firstValue(hero?.headline, prev.headline),
          subheadline: touched.has("subheadline")
            ? prev.subheadline
            : firstValue(hero?.subheadline, prev.subheadline),
          heroTagline: touched.has("heroTagline")
            ? prev.heroTagline
            : firstValue(hero?.tagline, prev.heroTagline),
          heroImageUri: touched.has("heroImageUri")
            ? prev.heroImageUri
            : firstValue(hero?.imageUrl, prev.heroImageUri),
          ctaText: touched.has("ctaText")
            ? prev.ctaText
            : firstValue(hero?.ctaText, prev.ctaText),

          shortDescription: touched.has("shortDescription")
            ? prev.shortDescription
            : firstValue(about?.shortDescription, prev.shortDescription),
          story: touched.has("story")
            ? prev.story
            : firstValue(about?.longDescription, prev.story),
          mission: touched.has("mission")
            ? prev.mission
            : firstValue(about?.mission, prev.mission),
          vision: touched.has("vision")
            ? prev.vision
            : firstValue(about?.vision, prev.vision),
          aboutImageUri: touched.has("aboutImageUri")
            ? prev.aboutImageUri
            : firstValue(about?.imageUrl, prev.aboutImageUri),

          services: touched.has("services")
            ? prev.services
            : firstArray(
                (servicesData?.items ?? []).map((s) => ({
                  title: s.title ?? "",
                  description: s.shortDescription ?? "",
                  imageUri: s.imageUrl ?? "",
                })),
                prev.services,
              ),
          features: touched.has("features")
            ? prev.features
            : firstArray(
                (featuresData?.items ?? []).map((f) => ({
                  title: f.title ?? "",
                  description: f.description ?? "",
                  imageUri: f.imageUrl ?? "",
                })),
                prev.features,
              ),
          stats: touched.has("stats")
            ? prev.stats
            : firstArray(
                (statsData?.items ?? []).map((s) => ({
                  type: s.label ?? "",
                  value: s.value ?? "",
                  suffix: s.suffix ?? "+",
                  label: s.label ?? "",
                })),
                prev.stats,
              ),
          testimonials: touched.has("testimonials")
            ? prev.testimonials
            : firstArray(
                (testimonialsData?.items ?? []).map((t) => ({
                  name: t.name ?? "",
                  role: t.role ?? "",
                  content: t.content ?? "",
                  avatarUri: t.avatarUrl ?? "",
                })),
                prev.testimonials,
              ),
          team: touched.has("team")
            ? prev.team
            : firstArray(
                (teamData?.items ?? []).map((t) => ({
                  name: t.name ?? "",
                  role: t.role ?? "",
                  bio: t.description ?? "",
                  avatarUri: t.imageUrl ?? "",
                })),
                prev.team,
              ),

          phone: touched.has("phone")
            ? prev.phone
            : firstValue(contact?.primaryPhone, prev.phone),
          email: touched.has("email")
            ? prev.email
            : firstValue(contact?.primaryEmail, prev.email),
          city: touched.has("city")
            ? prev.city
            : firstValue(contact?.city, prev.city),
          address: touched.has("address")
            ? prev.address
            : firstValue(contact?.address, prev.address),
          facebook: touched.has("facebook")
            ? prev.facebook
            : firstValue(contact?.socials?.facebook, prev.facebook),
          instagram: touched.has("instagram")
            ? prev.instagram
            : firstValue(contact?.socials?.instagram, prev.instagram),
          twitter: touched.has("twitter")
            ? prev.twitter
            : firstValue(contact?.socials?.twitter, prev.twitter),
          linkedin: touched.has("linkedin")
            ? prev.linkedin
            : firstValue(contact?.socials?.linkedin, prev.linkedin),
          youtube: touched.has("youtube")
            ? prev.youtube
            : firstValue(contact?.socials?.youtube, prev.youtube),
          faqs: touched.has("faqs")
            ? prev.faqs
            : firstArray(
                (faqData?.items ?? []).map((f) => ({
                  question: f.question ?? "",
                  answer: f.answer ?? "",
                })),
                prev.faqs,
              ),
        }));
      } catch {
        // fall back to workspace prefill silently
      } finally {
        if (!cancelled) setLoadingPrefill(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, websiteId, actorId]);

  const onChange = (key, value) => {
    touchedRef.current.add(key);
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 0) {
      if (!formData.businessName.trim())
        newErrors.businessName = "Business name is required";
      if (!formData.slug.trim()) newErrors.slug = "URL slug is required";
      else if (slugStatus === "taken")
        newErrors.slug = "This slug is already taken";
      else if (slugStatus === "checking")
        newErrors.slug = "Still checking slug availability…";
    }
    if (currentStep === 1 && !formData.headline.trim())
      newErrors.headline = "Headline is required";
    if (currentStep === 2 && !formData.shortDescription.trim())
      newErrors.shortDescription = "Short description is required";
    if (currentStep === 8) {
      if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
      if (!formData.email.trim()) newErrors.email = "Email address is required";
      if (!formData.city.trim()) newErrors.city = "City is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleGenerate();
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate(-1);
    } else {
      setCurrentStep((s) => s - 1);
    }
  };

  const runGenerate = async () => {
    const overrides = buildBusinessOverrides(formData);
    return mode === "regenerate" && websiteId
      ? olreadiaService.websites.regenerate(
          websiteId,
          templateId,
          overrides,
          actorId,
        )
      : olreadiaService.websites.bootstrap(
          {
            workspaceId: selectedWorkspaceId,
            name: formData.businessName,
            slug: formData.slug,
            description: formData.description,
            template: templateId,
            websiteType: "BUSINESS",
            overrides,
          },
          actorId,
        );
  };

  const handleGenerate = () => {
    if (!selectedWorkspaceId) return;
    setGenerateModalOpen(true);
  };

  const handleEditWebsite = (result) => {
    const nextWebsiteId = result.websiteId || websiteId || "";
    window.open(
      `https://dev.ovizia.com/editor/${nextWebsiteId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleClose = () => {
    setGenerateModalOpen(false);
    navigate(`/website?wid=${selectedWorkspaceId}`);
  };

  const stepProps = {
    data: formData,
    onChange,
    uploadingKey,
    progress,
    upload,
    slugStatus,
    slugMessage,
    mode,
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1Basics {...stepProps} />;
      case 1:
        return <Step2Hero {...stepProps} />;
      case 2:
        return <Step3About {...stepProps} />;
      case 3:
        return <Step4Services {...stepProps} />;
      case 4:
        return <Step5Features {...stepProps} />;
      case 5:
        return (
          <StatsStep
            stats={formData.stats}
            setStats={(v) => onChange("stats", v)}
          />
        );
      case 6:
        return <Step7Testimonials {...stepProps} />;
      case 7:
        return <Step8Team {...stepProps} />;
      case 8:
        return <Step9ContactFaq data={formData} onChange={onChange} />;
      default:
        return null;
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <>
      <WizardPageShell
        eyebrow="Build organization website"
        title={
          mode === "regenerate"
            ? "Update Website Info"
            : "Create Organization Website"
        }
        description={`${template?.name || templateId} • Add business details, contact, services, and story.`}
        onGenerate={handleGenerate}
        generating={false}
      >
        {loadingPrefill ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 80 }}
          >
            <Spin size="large" description="Loading your website content…" />
          </div>
        ) : (
          <div className="wiz-layout">
            <ProgressHeader
              steps={STEPS}
              currentStep={currentStep}
              onBack={handleBack}
            />

            <div className="wiz-body">
              <div className="wiz-step-header">
                <Text type="secondary">{STEPS[currentStep].description}</Text>
                {OPTIONAL_STEPS.includes(currentStep) ? (
                  <span className="wiz-optional-badge">
                    Optional — skip if you prefer
                  </span>
                ) : null}
              </div>

              <Card className="wiz-card-outer">{renderStep()}</Card>

              {Object.values(errors)
                .filter(Boolean)
                .map((err, i) => (
                  <Alert
                    key={i}
                    type="error"
                    showIcon
                    message={err}
                    className="wiz-error"
                  />
                ))}
            </div>

            <div className="wiz-footer">
              <span className="wiz-template-badge">
                🎨 {template?.name || templateId}
              </span>
              <div className="wiz-footer-actions">
                {currentStep > 0 ? (
                  <Button onClick={handleBack}>← Back</Button>
                ) : null}
                <Button type="primary" onClick={handleNext}>
                  {isLastStep ? "⚡ Generate Website" : "Next →"}
                </Button>
              </div>
              {!isLastStep ? (
                <button
                  type="button"
                  className="wiz-skip-link"
                  onClick={handleGenerate}
                >
                  Skip remaining steps and generate now
                </button>
              ) : null}
            </div>
          </div>
        )}
      </WizardPageShell>

      <WebsiteGenerateModal
        open={generateModalOpen}
        onClose={handleClose}
        template={template}
        run={runGenerate}
        onEdit={handleEditWebsite}
        getPreviewUrl={olreadiaService.websites.previewUrl}
      />
    </>
  );
}
